/**
 * Calibration Tools tab: a parameter-sweep test-grid generator over
 * TestGridSettings, exported as a ready-to-run .lbrn2, plus a short neutral
 * "how to calibrate" guide. MOPA colors are machine/material specific, so a
 * physical test grid is the reliable way to pin real parameters.
 */
import type { SweepParam, TestGridSettings } from "../../core/types";
import {
  button,
  checkboxField,
  clearNode,
  cutParamsEditor,
  dimField,
  el,
  section,
  selectField,
  textField,
  type PanelContext,
  type PanelHandle,
} from "../app";
import { MATERIAL_PRESETS, PRESET_DISCLAIMER } from "../presets";

const SWEEP_OPTS: { value: SweepParam; label: string }[] = [
  { value: "power", label: "Power (%)" },
  { value: "speed", label: "Speed (mm/s)" },
  { value: "frequencyKhz", label: "Frequency (kHz)" },
  { value: "qPulseNs", label: "Pulse width (Q-pulse, ns)" },
  { value: "intervalMm", label: "Line interval (mm)" },
];

function parseValues(text: string): { values: number[]; dropped: number } {
  const all = text
    .split(/[\s,]+/)
    .map((t) => parseFloat(t))
    .filter((v) => Number.isFinite(v));
  return { values: all.slice(0, 8), dropped: Math.max(0, all.length - 8) };
}

export function createToolsPanel(ctx: PanelContext): PanelHandle {
  const { store, actions } = ctx;
  const root = el("div");
  const s0 = store.get();

  const updateGrid = (patch: Partial<TestGridSettings>): void => {
    store.set((s) => ({ testGrid: { ...s.testGrid, ...patch } }));
    actions.persist();
    updateSummary();
  };

  // ---- sweep axes ---------------------------------------------------------
  const gridSection = section("Test grid", { open: true, hint: "Sweep any two parameters across a grid of swatches (max 8 × 8)." });

  let droppedNote = "";
  const applyValues = (key: "rowValues" | "colValues", text: string): void => {
    const { values, dropped } = parseValues(text);
    droppedNote = dropped > 0 ? `Only the first 8 ${key === "rowValues" ? "row" : "column"} values are used (${dropped} ignored).` : "";
    updateGrid({ [key]: values } as Partial<TestGridSettings>);
  };
  const rowValuesField = textField({
    label: "Row values (comma-separated)",
    value: s0.testGrid.rowValues.join(", "),
    title: "Up to 8 values.",
    onChange: (v) => applyValues("rowValues", v),
  });
  const colValuesField = textField({
    label: "Column values (comma-separated)",
    value: s0.testGrid.colValues.join(", "),
    title: "Up to 8 values.",
    onChange: (v) => applyValues("colValues", v),
  });

  gridSection.content.append(
    selectField<SweepParam>({ label: "Row parameter", value: s0.testGrid.rowParam, options: SWEEP_OPTS, onChange: (v) => updateGrid({ rowParam: v }) }).root,
    rowValuesField.root,
    selectField<SweepParam>({ label: "Column parameter", value: s0.testGrid.colParam, options: SWEEP_OPTS, onChange: (v) => updateGrid({ colParam: v }) }).root,
    colValuesField.root,
    el("div", { class: "field-row" }, [
      dimField(ctx, { label: "Swatch size", valueMm: s0.testGrid.swatchSizeMm, minMm: 1, stepMm: 1, title: "5–10 mm reads reliably; color needs a filled area.", onChangeMm: (v) => updateGrid({ swatchSizeMm: v }) }),
      dimField(ctx, { label: "Gap", valueMm: s0.testGrid.gapMm, minMm: 1, stepMm: 0.5, onChangeMm: (v) => updateGrid({ gapMm: v }) }),
    ]),
    checkboxField({ label: "Label each cell with its parameters", checked: s0.testGrid.labelCells, onChange: (v) => updateGrid({ labelCells: v }) }).root,
  );

  // ---- base params --------------------------------------------------------
  const baseSection = section("Base parameters", { open: true, hint: "Used for every field not being swept." });
  const baseWrap = el("div");
  function renderBase(): void {
    clearNode(baseWrap);
    baseWrap.append(cutParamsEditor(store.get().testGrid.base, (p) => updateGrid({ base: p })));
  }
  renderBase();

  // seed base from a preset color
  const presetSelect = selectField({
    label: "Material preset",
    title: "Seeds base cut parameters from a machine + material. Starting points only — calibrate before relying on them.",
    value: "0",
    options: MATERIAL_PRESETS.map((p, i) => ({ value: String(i), label: p.name })),
    onChange: () => rebuildColorOptions(),
  });
  const colorSelect = el("select") as HTMLSelectElement;
  function rebuildColorOptions(): void {
    clearNode(colorSelect);
    const preset = MATERIAL_PRESETS[Number(presetSelect.get())] ?? MATERIAL_PRESETS[0];
    preset.colors.forEach((c, i) => {
      const opt = el("option", { text: c.label }) as HTMLOptionElement;
      opt.value = String(i);
      colorSelect.append(opt);
    });
  }
  rebuildColorOptions();
  const seedBtn = button("Use color as base", {
    variant: "sm",
    onClick: () => {
      const preset = MATERIAL_PRESETS[Number(presetSelect.get())] ?? MATERIAL_PRESETS[0];
      const entry = preset.colors[Number(colorSelect.value)] ?? preset.colors[0];
      if (!entry) return;
      updateGrid({ base: { ...entry.params } });
      renderBase();
      actions.toast("success", `Base seeded from ${preset.name} · ${entry.label}.`);
    },
  });
  const colorField = el("div", { class: "field" }, [el("span", { class: "control-label", text: "Preset color" }), colorSelect]);
  baseSection.content.append(
    baseWrap,
    presetSelect.root,
    colorField,
    el("div", { class: "btn-row" }, [seedBtn]),
    el("p", { class: "disclaimer", text: PRESET_DISCLAIMER }),
  );

  // ---- export -------------------------------------------------------------
  const exportSection = section("Export", { open: true });
  const summary = el("div", { class: "notice" });
  const exportBtn = button("Export test grid (.lbrn2)", { variant: "primary full", onClick: () => actions.exportTestGrid() });
  exportSection.content.append(summary, exportBtn);

  function updateSummary(): void {
    const g = store.get().testGrid;
    const rows = g.rowValues.length;
    const cols = g.colValues.length;
    const valid = rows > 0 && cols > 0;
    exportBtn.disabled = !valid;
    if (!valid) {
      summary.className = "notice warn";
      summary.textContent = "Enter at least one row value and one column value.";
      return;
    }
    if (g.rowParam === g.colParam) {
      summary.className = "notice warn";
      summary.textContent = "Row and column parameters are the same — pick two different axes for a useful grid.";
      return;
    }
    summary.className = "notice accent";
    summary.textContent =
      `${rows} × ${cols} = ${rows * cols} swatches, ${g.swatchSizeMm} mm each with ${g.gapMm} mm gaps.` +
      (droppedNote ? ` ${droppedNote}` : "");
  }

  // ---- how to calibrate ---------------------------------------------------
  const howSection = section("How to calibrate", { open: false });
  const how = el("div", { class: "how-to" });
  how.innerHTML =
    "<p>MOPA colors depend on your machine, lens, focus, and material — no preset transfers exactly. Dial them in with a physical test grid:</p>" +
    "<ol>" +
    "<li>Clean the metal thoroughly (90%+ isopropyl alcohol, wear gloves — fingerprints ruin color).</li>" +
    "<li>Recheck focus; some colors need a deliberate focus offset.</li>" +
    "<li>Run this grid on scrap of the same material and batch.</li>" +
    "<li>Photograph it under neutral light and note the parameters of the swatches you like.</li>" +
    "<li>Enter those values as a layer's cut parameters, then export your job with them embedded.</li>" +
    "</ol>" +
    "<p>Keep the field within roughly 60 × 60 mm of the lens center, and start a sweep by varying frequency and interval before power.</p>";
  howSection.content.append(how);

  root.append(gridSection.root, baseSection.root, exportSection.root, howSection.root);
  updateSummary();

  return { element: root };
}
